import { StorageApiError, StorageError, isStorageError } from './supabase/errors'
import { Result, FetchParameters } from './types'

/**
 * 客户端运行上下文：`{ config, request }`。
 */
export interface Context {
  config: any
  request: any
}

/** 网关相对前缀：GATEWAY base 已含 `/v1`，原始 fetch 用 origin，需补 `/v1/storages`。 */
const STORAGE_API_PREFIX = '/v1/storages'

function buildQueryString(query?: Record<string, any>): string {
  if (!query) return ''
  const parts: string[] = []
  Object.keys(query).forEach((key) => {
    const value = query[key]
    if (value === undefined || value === null) return
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
  })
  return parts.join('&')
}

/**
 * 统一执行操作并返回 `{ data, error }`；`throwOnError` 开启时失败抛异常，成功仍返回 `{ data, error: null }`。
 */
export async function handleOperation<T>(fn: () => Promise<T>, shouldThrowOnError: boolean): Promise<Result<T>> {
  try {
    const data = await fn()
    return { data, error: null }
  } catch (error: any) {
    if (shouldThrowOnError) throw error
    if (isStorageError(error)) {
      return { data: null, error }
    }
    return { data: null, error: new StorageError(error?.message || String(error)) }
  }
}

/**
 * Storage HTTP 请求封装。
 *
 * - JSON 端点：`request.fetch`（自动注入 `Authorization`，`shouldThrowOnError: false` 以便手动解析错误码）。
 * - Blob 下载：`reqClass.get`（避免 Node 适配器 `fetch` 强制 `res.json()` 破坏二进制流）。
 */
export class Http {
  constructor(public readonly ctx: Context) {}

  private get request() {
    return this.ctx.request
  }

  /** 网关 origin（如 `https://{env}.api.tcloudbasegateway.com`，不含 `/v1`）。 */
  gatewayOrigin(): string {
    return this.request.getBaseEndPoint('GATEWAY')
  }

  /** 拼接完整 API URL。 */
  buildUrl(path: string, query?: Record<string, any>): string {
    let url = `${this.gatewayOrigin()}${STORAGE_API_PREFIX}${path}`
    const qs = buildQueryString(query)
    if (qs) {
      url += (url.includes('?') ? '&' : '?') + qs
    }
    return url
  }

  /** 把后端返回的相对 `signedURL` 拼成完整 URL。 */
  toFullUrl(maybeRelative: string): string {
    if (!maybeRelative) return maybeRelative
    if (/^https?:\/\//.test(maybeRelative) || maybeRelative.startsWith('//')) {
      return maybeRelative
    }
    return `${this.gatewayOrigin()}${STORAGE_API_PREFIX}${maybeRelative.startsWith('/') ? '' : '/'}${maybeRelative}`
  }

  /** JSON / 表单 / 二进制请求，返回解析后的 body；非 2xx 抛 StorageApiError。 */
  async send<T = any>(opts: {
    method: string
    path: string
    /** JSON body（会被 stringify）。与 rawBody 互斥。 */
    json?: any
    /** 原始 body（FormData / Blob / 二进制 / 字符串），用于上传。 */
    rawBody?: any
    headers?: Record<string, string>
    query?: Record<string, any>
    /** 是否携带登录态，默认 true。签名上传 / 公开下载传 false。 */
    withCredentials?: boolean
    /** 请求取消信号，透传给底层 fetch。 */
    signal?: AbortSignal
    /** fetch 缓存策略，透传给底层 fetch。 */
    cache?: FetchParameters['cache']
  }): Promise<T> {
    const url = this.buildUrl(opts.path, opts.query)
    const method = opts.method.toUpperCase()
    const isBodyless = ['GET', 'HEAD'].includes(method)

    const headers: Record<string, string> = { ...(opts.headers || {}) }
    let body: any
    if (!isBodyless) {
      if (opts.rawBody !== undefined) {
        body = opts.rawBody
      } else if (opts.json !== undefined) {
        headers['Content-Type'] = headers['Content-Type'] || 'application/json'
        body = JSON.stringify(opts.json)
      }
    }

    const res = await this.request.fetch({
      url,
      method,
      headers,
      ...(body !== undefined ? { body } : {}),
      withCredentials: opts.withCredentials !== false,
      shouldThrowOnError: false,
      ...(opts.signal ? { signal: opts.signal } : {}),
      ...(opts.cache ? { cache: opts.cache } : {}),
    })

    return this.unwrap<T>(res, url)
  }

  /**
   * 下载，返回原始 `Response`。走 `request.fetch({ stream: true })` 让适配器透出未消费的原生 `Response`。
   *
   * 上层按终态决定如何消费——`response.blob()` 得 `Blob`，或 `response.body` 得 `ReadableStream`。
   *
   * @remarks 依赖底层适配器 `fetch` 透出 `response`（web / Node 适配器均已支持）。
   */
  async download(opts: {
    path: string
    headers?: Record<string, string>
    query?: Record<string, any>
    withCredentials?: boolean
    /** 请求取消信号，透传给底层 fetch。 */
    signal?: AbortSignal
    /** fetch 缓存策略，透传给底层 fetch。 */
    cache?: FetchParameters['cache']
  }): Promise<{ response: Response; status: number; header: any }> {
    const url = this.buildUrl(opts.path, opts.query)

    const res = await this.request.fetch({
      url,
      method: 'GET',
      headers: { ...(opts.headers || {}) },
      stream: true,
      withCredentials: opts.withCredentials !== false,
      shouldThrowOnError: false,
      ...(opts.signal ? { signal: opts.signal } : {}),
      ...(opts.cache ? { cache: opts.cache } : {}),
    })

    // signal 被中断时，底层可能 resolve 出空响应；这里显式抛出中断错误，避免误判为成功。
    if (opts.signal?.aborted) {
      throw new StorageApiError('The download was aborted', 0, 'STORAGE_PG_ABORTED')
    }

    const status = Number(res?.statusCode ?? 200)
    if (status === 304) {
      throw new StorageApiError('Not Modified', status, 'STORAGE_PG_NOT_MODIFIED')
    }
    if (status >= 400) {
      throw new StorageApiError(`Download failed with status ${status}`, status, 'STORAGE_PG_REQUEST_FAIL')
    }

    const response: Response = res?.response
    if (!response) {
      throw new StorageApiError('Underlying adapter did not expose the raw Response', status, 'STORAGE_PG_NO_RESPONSE')
    }
    return { response, status, header: res?.header }
  }

  private async unwrap<T>(res: any, url: string): Promise<T> {
    const status = Number(res?.statusCode ?? res?.status ?? 200)
    let body = res?.data
    try {
      body = await body
    } catch (e) {
      body = undefined
    }

    if (status >= 200 && status < 300) {
      return body as T
    }

    // 兼容两类错误体：ErrorResponse { code, message, requestId } 与 StreamErrorResponse { statusCode, error, message }
    const code = body?.code || body?.error || 'STORAGE_PG_REQUEST_FAIL'
    const message = body?.message || `Storage request failed with status ${status}: ${url}`
    throw new StorageApiError(message, status, code)
  }
}
